<template>
    <div class="email-content-widget">
        <div class="widget-header">
            <h3>{{ steps[step].title }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="step === 'create'" class="create-step">
                <app-text-editor 
                    ref="textEditor" 
                    v-model="body"
                    :config="froalaConfig"
                    :userId="null"
                    :isLoadingContent="isProcessingContent"
                    :clientId="clientId"
                    :addOns="addOns"
                    :injectOptions="['Link', 'Inline']"
                    :showBrandingColors="false"
                    @insertSignature="insertSignature($event)" />
            </div>
            <div v-else-if="step === 'saving'" class="saving-step">
                <loading-screen-svg />
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Cancel</app-button>
            </div>
            <div class="action-container" >
                <app-button class="secondary-btn" @click="openSendTestWidget">Send test email</app-button>
                <app-button @click="steps[step].action" :loading="steps[step].loading">
                    {{ steps[step].buttonText }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppTextEditor = () => import('@/components/common/app-text-editor');

import EventBus from '@/scripts/event-bus.js';
import { LogLevel } from "@/scripts/logger";

export default {
    name: 'email-content-widget',

    props: {
        widget: {
            type: Object   
        }
    },

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-text-editor': AppTextEditor,
        'loading-screen-svg': LoadingScreenSvg
    },

    data() {
        return {
            isProcessingContent: false,
            linkAttachments: [],
            campaignTemplateId: this.widget.campaignTemplateId,
            campaignId: this.widget.campaignId,
            clientId: null,
            version: this.widget.version || 'A',
            body: '',
            step: 'create',
            steps: {
                create: {
                    title: 'Campaign Email Content',
                    buttonText: 'Save',
                    action: this.saveEmail,
                    loading: false
                },

                saving: {
                    title: 'Saving',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true
                }
            },

            froalaConfig: {
                key: this.$helpers.getFroalaKey(),
                toolbarButtons: ['fontFamily', 'insert', 'fontSize', 'align', 'formatUL', 'emoticons', 'bold', 'italic', 'colorPicker', 'insertImage', 'insertLink', 'highlight'],
                
                useClasses: false,
                fontFamilySelection: true,
                fontSizeSelection: true,
                paragraphFormatSelection: true,
                fontFamily: this.$helpers.minimalFontStyles,
                emoticonsUseImage: false,

                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: 'https://api.honeycrm.com/api/Files/Froala',
                fileUploadURL: 'https://api.honeycrm.com/api/Files/Froala',

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 4 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],

                requestHeaders: {
                    Authorization: `Bearer ${ localStorage.jwt }`
                }
            }
        }
    },

    computed: {
        addOns() {
            var addOns = ['library'];

            if (this.clientId) {
                addOns.push('teamSignatures');
            }
            
            return addOns;
        }
    },

    methods: {
        saveEmail() {
            this.saveBody()
                .then(this.closeWidget);
        },

        addAttachmentsFromLibrary(payload) {
            var formattedAttachments = this.formatAttachmentsFromLibrary(payload.selectedItems);

            this.processWebPageLinks(formattedAttachments);

            var nonWebPageAttachments = this.getNonWebPageAttachments(formattedAttachments);
               
            if(payload.attachmentType.toUpperCase() == 'LINK') {
                this.addLinkAttachments(nonWebPageAttachments);
            }
            else if(payload.attachmentType.toUpperCase() == 'INLINE') {
                var inlineAttachments = this.getInlineAttachments(formattedAttachments);
                var linkAttachments = this.getNonInlineAttachments(formattedAttachments);

                this.addInlineAttachments(inlineAttachments);
                this.addLinkAttachments(linkAttachments);
            }
        },

        formatAttachmentsFromLibrary(rawAttachments) { 
            var formattedAttachments = rawAttachments.map(attachment => {
                return {
                    id: attachment.id,
                    guid: attachment.guid,
                    name: attachment.name,
                    fileType: attachment.fileType,
                    url: attachment.url,
                    description: attachment.description,
                    domain: attachment.domain
                }
            });

            return formattedAttachments;
        },

        processWebPageLinks(attachments) {
            var webPageAttachments = this.getWebPageAttachments(attachments);
            if(webPageAttachments.length > 0) {
                this.addAsWebPageAttachments(webPageAttachments);
            }
        },

        getWebPageAttachments(attachments) {
            var webPageAttachments = attachments.filter((attachment) => {
                return attachment.fileType.toUpperCase() == 'LINK';
            });

            return webPageAttachments;
        },

        addAsWebPageAttachments(attachments) {
            var request = {
                Links: []
            };

            request.Links = attachments.map(attachment => {
                return {
                    Url: attachment.url
                }
            })

            this.isProcessingContent = true;

            this.$api.post('/api/Collaterals/Links/Enrich', request)
                .then(response => {
                    var enrichedLinks = this.mapEnrichedLinks(attachments, response.data.Links);
                    this.addEnrichedLinks(enrichedLinks);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Unable to attach', { error })
                })
                .finally(() => {
                    this.isProcessingContent = false;
                });
        },

        mapEnrichedLinks(attachments, links) {
            var enrichedLinks = [];

            for(var i = 0; i < links.length; i++) {
                var link = links[i];
                for(var j = 0; j < attachments.length; j++) {
                   var attachment = attachments[j];
                   if(attachment.url == link.Url) {
                        var enrichedLink = {
                            name: attachment.name ? attachment.name : link.Name,
                            title: attachment.title ? attachment.title : link.Title,
                            fileType: link.FileType,
                            domain: link.Domain,
                            url: link.Url,
                            guid: attachment.guid,
                            description: null,
                            uploadedByUserId: this.userId,
                            clientId: this.clientId,
                            agencyId: this.agencyId
                        };

                        enrichedLink.description = this.limitDescription(link.Description || '');

                        enrichedLinks.push(enrichedLink); 
                        break;
                   }
                }
            }

            return enrichedLinks;
        },

        limitDescription(description) {
            var limitedDescription = description.replace(/(<([^>]+)>)/ig,"");

            if(limitedDescription.length > 130) {
                var characterDelta = limitedDescription.length - 127;
                limitedDescription = limitedDescription.slice(0, limitedDescription.length - characterDelta);
                limitedDescription += '...';
            }

            return limitedDescription;
        },

        addEnrichedLinks(links) {
            var insert = links.map(link => {
                var proxyUrl = this.$helpers.getFilesTrackingUrl(link.guid);
                return `<a href="${ proxyUrl }" style="cursor:pointer;text-decoration:none;display:inline-block;padding-top:0px;padding-bottom:0px;padding-right:0px;padding-left:0px;vertical-align:middle;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin:0px;color:#18AEFF;font-size:14px;" target="_blank">${ link.title }</a><br/>`;
            }).join('');

            this.body += insert;
        },    

        getNonWebPageAttachments(attachments) {
            var nonWebPageAttachments = attachments.filter((attachment) => {
                return attachment.fileType.toUpperCase() != 'LINK';
            });

            return nonWebPageAttachments;
        },

        addLinkAttachments(attachments) {
            this.linkAttachments = this.linkAttachments.concat(attachments);
            var insert = this.linkAttachments.map(attachment => {
                var proxyUrl = this.$helpers.getFilesTrackingUrl(attachment.guid);
                return `<a href="${ proxyUrl }" style="cursor:pointer;text-decoration:none;display:inline-block;padding-top:0px;padding-bottom:0px;padding-right:0px;padding-left:0px;vertical-align:middle;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin:0px;color:#18AEFF;font-size:14px;" target="_blank">
                        ${ attachment.name }
                    </a><br/>`;
            }).join('');

            this.body += insert;

            this.linkAttachments = [];
        },

        getInlineAttachments(attachments) {
            var inlineAttachments = attachments.filter((attachment) => {
                return attachment.fileType.toUpperCase() == 'IMAGE';
            });

            return inlineAttachments;
        },

        getNonInlineAttachments(attachments) {
            var nonInlineAttachments = attachments.filter((attachment) => {
                return attachment.fileType.toUpperCase() != 'IMAGE';
            });

            return nonInlineAttachments;
        },

        addInlineAttachments(inlineAttachments) {
            for(var i = 0; i < inlineAttachments.length; i++) {
                this.body += `<img src="${inlineAttachments[i].url}"/>`;
            }
        },

        getCampaign() {
            this.$api
                .get(`/service/CampaignTemplates/?$filter=Id eq ${ this.campaignTemplateId }&$select=ConfigurationA,ConfigurationB,ClientId`)
                .then(response => {
                    this.clientId = response.data.value[0].ClientId;

                    if (this.version === 'B') {
                        this.body = response.data.value[0].ConfigurationB ? JSON.parse(response.data.value[0].ConfigurationB).body : '';
                    }
                    else {
                        this.body = response.data.value[0].ConfigurationA ? JSON.parse(response.data.value[0].ConfigurationA).body : '';
                    }
                });
        },

        saveBody() {    
            return new Promise((resolve, reject) => {        
                if (this.body) {
                    this.step = 'saving';

                    var changes = [{
                        'name': this.version === 'B' ? 'ConfigurationB' : 'ConfigurationA',
                        'value': JSON.stringify({ body: this.body })
                    }];

                    this.$api
                        .patch(`/api/CampaignTemplates/${ this.campaignTemplateId }`, changes)
                        .then(() => {
                            this.$helpers.showSuccessNotification('Content successfully saved.');
                            EventBus.$emit('campaign-configuration:update', { body: this.body });
                            resolve();
                        })
                        .catch((error) => {
                            this.$helpers.showFailNotification('Failed to save content.', { error });
                            reject();
                        })
                        .finally(() => {
                            this.step = 'create';
                        });
                } 
                else {
                    this.$helpers.showFailNotification('Add some content before saving.', { level: LogLevel.INFO });
                    reject();
                }
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        insertSignature(signature) {
            this.body += signature;
        },

        openSendTestWidget(){
            this.saveBody()
                .then(()=>{
                    EventBus.$emit('open-widget', {
                        name: 'email-test-widget',
                        campaignTemplateId: this.campaignTemplateId,
                        campaignId: this.campaignId,
                    });
                });
        }
    },

    created() {
        this.getCampaign();
        EventBus.$on('library-payload-emitted', this.addAttachmentsFromLibrary);
    },

    beforeDestroy() {
        EventBus.$off('library-payload-emitted');
    }
}
</script>

<style scoped>
.email-content-widget .saving-step {
    text-align: center;
}

.email-content-widget .content-wrapper {
    padding: 0;
}

.email-content-widget .action-container {
    display: flex;
}

.email-content-widget .action-container .btn {
    margin-left: 15px;
}

.email-content-widget .action-container .secondary-btn {
    background: transparent;
    color: #877D88;
	border: 2px solid #877D88;
}

.email-content-widget .action-container .secondary-btn:hover {
    background: #E1E0E5;
}

.email-content-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877D88;
}

.email-content-widget .cancel-container .btn.transparent-btn:hover {
    background: #E1E0E5;
}

.email-content-widget .action-container .btn.white-btn {
    background: #FFFFFF;
    color: #877D88;
    border: 2px solid #E9E9EF;
}
</style>
